import backendApi from '../axios/backendApi'
import { computed } from 'vue';
import { todaycode } from './utils'
import { useDataStore } from '../stores/useDataStore';
import { useToast } from 'vue-toastification'
import { computeAbsDelta } from './functions_pilotage';

const toast = useToast()

export const updateVolumeTraite = (tempPrelevement: Prelevement, input: string) => {
  try {
    tempPrelevement.vol_traite = eval(input) || 0;
  } catch {
    tempPrelevement.vol_traite = 0;
  }
};

export const updateAbsorbance = (tempPrelevement: Prelevement, input: string) => {
  try {
    tempPrelevement.absorbance_880nm = eval(input) || 0;
  } catch {
    tempPrelevement.absorbance_880nm = 0;
  }
};

export function getObservationValue(prelevement: Prelevement, observationTypeId: number): number | null {
  const found = prelevement.observation_values.find(o => o.observation_type_id === observationTypeId);
  return found?.value ?? null;
}

// Computed property to filter and sort prelevements based on selected culture
export function useRecoltePrelevements() {
  const dataStore = useDataStore();

  const filteredPrelevements = computed(() => {
    if (!dataStore.selectedCulture) {
      return { data: [], noData: true };
    }

    const prelevements = dataStore.selectedCulture.prelevements

    if (prelevements.length === 0) {
      return { data: [], noData: true };
    }

    // Tri...
    const sorted = prelevements.sort((a, b) => {
      const daycodeA = a.daycode.split('-').map(Number);
      const daycodeB = b.daycode.split('-').map(Number);
      return daycodeA[0] === daycodeB[0]
        ? daycodeA[1] - daycodeB[1]
        : daycodeA[0] - daycodeB[0];
    });

    return { data: sorted, noData: false };
  });


  return { filteredPrelevements };
}

// Calcul du delta de l'absorbance entre un prélèvement et le précédent
export const calculateDelta = (index: number) => {
  const { filteredPrelevements } = useRecoltePrelevements();

  if (index === 0) return '';

  const previous = filteredPrelevements.value.data[index - 1];
  const current = filteredPrelevements.value.data[index];

  const delta = computeAbsDelta(previous, current, null);
  return delta !== null ? delta.toFixed(2) : '';
};

export const calculateDeltaForDisplay = (displayIndex: number) => {
  const { filteredPrelevements } = useRecoltePrelevements();
  const chronoIndex = filteredPrelevements.value.data.length - 1 - displayIndex;
  return calculateDelta(chronoIndex);
};

export function useRecoltePrelevementsForDisplay() {
  const { filteredPrelevements } = useRecoltePrelevements();
  const inversedPrelevements = computed(() => {
    // Crée une copie inversée du tableau pour l'affichage
    return [...filteredPrelevements.value.data].reverse();
  });
  return { inversedPrelevements };
}

export const handleSubmit = async (prelevement: Prelevement, t: (key: string) => string) => {
  const dataStore = useDataStore();

  try {
    if (!dataStore.selectedCulture) {
      console.error("Aucune culture sélectionnée !");
      return;
    }

    prelevement.culture_id = dataStore.selectedCulture.id;

    const { id, ...basePayload } = prelevement;
    const payload = {
      ...basePayload,
      observations: prelevement.observation_values?.map(obs => ({
        observation_type_id: obs.observation_type_id,
        value: obs.value
      })) ?? []
    };

    if (dataStore.isEditing && dataStore.editId !== null) {
      await backendApi.put(`/prelevements/${dataStore.editId}`, payload);

      const index = dataStore.selectedCulture.prelevements.findIndex(p => p.id === dataStore.editId);
      if (index !== -1) {
        dataStore.selectedCulture.prelevements[index] = { ...prelevement, id: dataStore.editId };
      }
    } else {
      const response = await backendApi.post(`/prelevements`, payload);
      const newPrelevement = response.data;

      dataStore.selectedCulture.prelevements.push(newPrelevement);
    }

    toast.success(t('prelevementSavedSuccess'))

    resetForm(prelevement);
    dataStore.isEditing = false;
    dataStore.editId = null;
  } catch (error) {
    console.error(t('prelevementSavedError'), error);
    toast.error(t('prelevementSavedError'))
  }
};

export const editPrelevement = (p: Prelevement, tempPrelevement: Prelevement) => {
  const dataStore = useDataStore();
  dataStore.isEditing = true;
  dataStore.editId = p.id ?? null;

  // Copie les données dans le formulaire temporaire
  Object.assign(tempPrelevement, {
    ...p,
    observations: p.observation_values?.map(obs => ({
      observation_type_id: obs.observation_type_id,
      value: obs.value,
      observation_type: obs.observation_type ?? null,
    })) ?? []
  });

};


// Fonction pour supprimer un prélèvement
export const deletePrelevement = async (id: number, t: (key: string) => string) => {
  const dataStore = useDataStore();
  try {
    await backendApi.delete(`/prelevements/${id}`);
    // Supprimer localement le prélèvement de la liste
    if (dataStore.selectedCulture) {
      dataStore.selectedCulture.prelevements = dataStore.selectedCulture.prelevements.filter(p => p.id !== id);
    }
    toast.success(t('prelevementDeletedSuccess'))
  } catch (error) {
    console.error(t('prelevementDeletedError'), error);
    toast.error(t('prelevementDeletedError'))
  }
};

export const resetForm = (tempPrelevement: Prelevement) => {
  const dataStore = useDataStore();

  tempPrelevement.id = 0;
  tempPrelevement.culture_id = 0;
  tempPrelevement.daycode = todaycode;
  tempPrelevement.user = null;
  tempPrelevement.commentaires = null;
  tempPrelevement.absorbance_880nm = null;
  tempPrelevement.vol_recolte = null;
  tempPrelevement.ph_lu = null;
  tempPrelevement.ph_mesure = null;
  tempPrelevement.vol_traite = 0;
  tempPrelevement.masse_residuelle = null;
  tempPrelevement.temperature = null;

  // Réinitialise les observations dynamiques
  if (dataStore.observationTypes?.length) {
    tempPrelevement.observation_values = dataStore.observationTypes.map(type => ({
      observation_type_id: type.id,
      value: null,
      observation_type: type
    }));
  } else {
    tempPrelevement.observation_values = [];
  }

  dataStore.isEditing = false;
  dataStore.editId = null;
};

export const getAbsorbanceCellStyle = (volRecolte: number | null) => {
  // Si volRecolte est null, le considérer comme 0
  const value = volRecolte ?? 0;
  // Si volRecolte est différent de 0, on applique le fond jaune
  return value !== 0 ? 'background-color: lightgreen;' : '';
};

// Computed property to determine if the table should be shown
export const showTable = computed(() => {
  const dataStore = useDataStore();
  return dataStore.selectedCulture;
});